import { defaultStoreUtils } from '@databases/redux';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { container, getService } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import { isEmptyValue } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import useView from '@databases/views/useView';
import { useCallback, useEffect } from 'react';

import { copyHtmlTableToClipboard, copyToClipboard } from '../helpers/copy-helper';
import { isEmptyClipboard } from '../helpers/is-empty-clipboard';

import { useCopyWithDsp } from './useCopyWithDsp';

interface UseCopyDatabaseContentProps {
  contentId: string;
}

type CopyHandler = (event: ClipboardEvent) => void;

const getEditableTarget = (target: EventTarget | null): HTMLElement | undefined => {
  if (!target || !(target instanceof HTMLElement)) {
    return undefined;
  }

  const editableTarget = target.closest(
    'input, textarea, [contenteditable], .ProseMirror, [role="textbox"]'
  );

  if (!editableTarget || !(editableTarget instanceof HTMLElement)) {
    return undefined;
  }

  if (editableTarget instanceof HTMLInputElement) {
    return editableTarget.type !== 'checkbox' && editableTarget.type !== 'radio'
      ? editableTarget
      : undefined;
  }

  if (editableTarget instanceof HTMLTextAreaElement) {
    return editableTarget;
  }

  const contentEditable = editableTarget.getAttribute('contenteditable');
  return contentEditable !== 'false' ? editableTarget : undefined;
};

const hasEditableSelection = (target: EventTarget | null): boolean => {
  const editableTarget = getEditableTarget(target);

  if (!editableTarget) {
    return false;
  }

  if (
    editableTarget instanceof HTMLInputElement ||
    editableTarget instanceof HTMLTextAreaElement
  ) {
    return editableTarget.selectionStart !== editableTarget.selectionEnd;
  }

  const selection = globalThis.getSelection?.();
  return (
    selection?.type === 'Range' &&
    !!selection.anchorNode &&
    !!selection.focusNode &&
    editableTarget.contains(selection.anchorNode) &&
    editableTarget.contains(selection.focusNode)
  );
};

const shouldDatabasesHandleCopy = (target: EventTarget | null) => {
  const selectionsReader = getService('SelectionsReader');
  return selectionsReader.hasSelection() && !hasEditableSelection(target);
};

/**
 * Registers a document event listener that reacts to copy events.
 * This is used to copy the selected database content to the clipboard.
 */
export const useCopyListener = ({ contentId }: UseCopyDatabaseContentProps) => {
  const { view } = useView();
  const store = defaultStoreUtils.useStore();
  const { attemptCopy } = useCopyWithDsp();

  const areNonEmptyAttachmentsSelected = useCallback(() => {
    if (contentId) {
      const state = store.getState();
      const database = getDatabaseWithCurrentView(state, {
        contentId,
      });

      const selectionsReader = getService('SelectionsReader');
      const selectedValues = selectionsReader.getSelectedValues();
      const selectedAttachmentValues = selectedValues.filter(
        (value) =>
          getDatabaseFieldRequired(database, value.fieldId).type ===
          DatabaseFieldType.ATTACHMENT
      );

      return selectedAttachmentValues.some((selectedValue) => {
        const field = getDatabaseFieldRequired(database, selectedValue.fieldId);
        const entry = getDatabaseEntryRequired(database, selectedValue.entryId);
        const value = getDatabaseValue(database, field, entry);
        return !isEmptyValue({ database, field, entry, value }, state);
      });
    }
    return false;
  }, [contentId, store]);

  const handleExportDatabaseContent = useCallback(
    (state: ReturnType<(typeof store)['getState']>) => {
      copyHtmlTableToClipboard(contentId, state, view.settings);
    },
    [contentId, view.settings]
  );

  /**
   * @returns whether the callback handled the copy event or not
   */
  const handleCopyInsideDatabase = useCallback(
    (e: ClipboardEvent) => {
      const state = store.getState();

      if (shouldDatabasesHandleCopy(e.target)) {
        e.preventDefault();
        handleExportDatabaseContent(state);
        return true;
      } else {
        return false;
      }
    },
    [handleExportDatabaseContent, store]
  );

  const copyListener: CopyHandler = useCallback(
    (e) => {
      const hasNonEmptyAttachments = areNonEmptyAttachmentsSelected();

      const handledByDatabasesOrDsp = attemptCopy(() => {
        return handleCopyInsideDatabase(e);
      }, hasNonEmptyAttachments);

      if (handledByDatabasesOrDsp) {
        return;
      }

      const editableTarget = getEditableTarget(e.target);
      if (editableTarget) {
        // Inside the databases iframe the browser's built-in copy does nothing for a
        // plain <input>/<textarea>, so copy the highlighted text ourselves. Rich-text
        // editors (contenteditable/ProseMirror) handle their own copy, so leave them be.
        if (
          editableTarget instanceof HTMLInputElement ||
          editableTarget instanceof HTMLTextAreaElement
        ) {
          const { selectionStart, selectionEnd, value } = editableTarget;
          const selectedText = value.slice(selectionStart ?? 0, selectionEnd ?? 0);
          if (selectedText.length > 0) {
            e.preventDefault();
            void copyToClipboard(selectedText, 'text/plain');
          }
        }
        return;
      }

      if (!isEmptyClipboard(e)) {
        // Still gotta make sure that if there's any data in the payload, we let
        // the browser decide what to do - We only want to handle empty payloads
        return;
      }

      e.preventDefault();
      // Forward to Whiteboards when databases doesn't handle it (empty payload case)
      const eventBus = container.resolve('EventBus');
      eventBus.dispatch('forwardCopyClipboardToParent', null);
    },
    [attemptCopy, handleCopyInsideDatabase, areNonEmptyAttachmentsSelected]
  );

  useEffect(() => {
    document.addEventListener('copy', copyListener);

    return () => {
      document.removeEventListener('copy', copyListener);
    };
  }, [copyListener]);
};
